import createRaf from "@solid-primitives/raf";
import { ReactiveSet } from "@solid-primitives/set";
import { createMemo, createSignal, For, onCleanup, onMount } from "solid-js";
import { openWithdrawPrompt, tradeIn } from "~/js/casino";
import { game } from "~/js/game";
import { getCurrency } from "~/js/prefs";
import { SoundManager } from "~/js/sounds";
import { addStat } from "~/js/stats";
import { createPage } from "~/js/utils";
import * as styles from "~/pages/roulette/roulette.css";
import { global, goToPreviousPage } from "~/script";
import { user } from "~/userStore";

function RoulettePage() {
	let canvas: HTMLCanvasElement;
	let canvasContext: CanvasRenderingContext2D;

	let winningIndex: number | null = null;
	let spinDuration = 0;
	let spinProgress = 0;
	let wheelRotation = 0;
	let lastTimestamp = 0;
	let lastTickIndex = 0;
	let targetRotation = 0;
	let betnumbers: number[] = [];
	const [isShiftPressed, setIsShiftPressed] = createSignal(false);
	const [betAmount, setBetAmount] = createSignal(Math.min(user.tokens, Math.floor((user.upgrades.maxbet / 100) * 8)));
	const selectedNumbers = new ReactiveSet<number>();
	const [gameState, setGameState] = createSignal<"idle" | "spinning">("idle");
	const [betInfo, setBetInfo] = createSignal<string>("Please enter a bet above.");

	const [rouletteHistory, setRouletteHistory] = createSignal<
		{ number: number; color: "green" | "black" | "red"; result: "win" | "loss" }[]
	>([]);

	function setBetAmount2(amount: number) {
		if (gameState() === "spinning") return;
		amount = Math.min(amount, user.tokens);
		amount = Math.min(amount, Math.floor((user.upgrades.maxbet / 100) * 8));

		setBetAmount(Math.round(amount));
		setBetInfo(`You can only bet on one field. You are currently betting ${betAmount().toLocaleString()} chips.`);
	}

	const payoutMultiplier = createMemo(() => {
		switch (selectedNumbers.size) {
			case 1:
				return 35;
			case 12:
				return 2;
			case 18:
				return 1;
			default:
				return null;
		}
	});

	function startSpin() {
		if (gameState() === "idle" && betAmount() <= user.tokens && payoutMultiplier()) {
			setGameState("spinning");
			global.noSpoilers = true;
			user.tokens -= betAmount();
			addStat("spend_chips_on_roulette", betAmount());
			addStat("spend_chips", betAmount());
			betnumbers = Array.from(selectedNumbers);
			game.emit("update");
			winningIndex = Math.floor(Math.random() * wheelNumbers.length);
			targetRotation = (Math.PI / 3) * 4 + ((Math.PI * 2) / wheelNumbers.length / 3) * (winningIndex + 0.1 + Math.random() * 0.8 - 0.5);
			spinDuration = isShiftPressed() ? 1500 : 10000;
			spinProgress = 0;
		}
	}

	function endSpin() {
		const totalPayout = payoutMultiplier()! + 1;
		const winningPocket = wheelNumbers[winningIndex!]!;
		let win: "win" | "loss";
		targetRotation = 0;
		if (betnumbers.includes(winningPocket.number)) {
			user.tokens += betAmount() * totalPayout;
			setBetInfo(`You won ${betAmount() * totalPayout} chips!`);
			addStat("roulette_wins", 1);
			addStat("win_chips", betAmount() * totalPayout);
			addStat("roulette_winnings", betAmount() * totalPayout);
			if (totalPayout === 3) addStat("roulette_wins_2to1", 1);
			if (totalPayout === 36) addStat("roulette_wins_35to1", 1);
			win = "win";
		} else {
			setBetInfo("Bad luck! You lost.");
			win = "loss";
		}

		setRouletteHistory((h) => [{ number: winningPocket.number, color: winningPocket.color, result: win }, ...h].slice(0, 20));

		global.noSpoilers = false;
		game.emit("update");

		setGameState("idle");
	}

	function animateWheel(timestamp: DOMHighResTimeStamp = 0) {
		const deltaTime = timestamp - lastTimestamp || 0;
		lastTimestamp = timestamp;

		const canvasWidth = canvas.clientWidth * devicePixelRatio;
		canvas.width = canvasWidth;
		canvas.height = canvas.clientHeight * devicePixelRatio;

		canvasContext.scale(devicePixelRatio, devicePixelRatio);
		canvasContext.translate(canvasWidth / (2 * devicePixelRatio), 200);

		if (gameState() === "idle") {
			wheelRotation -= deltaTime / 20000;
		} else if (gameState() === "spinning") {
			spinProgress += deltaTime;
			const progress = spinProgress >= spinDuration ? 1 : spinProgress / spinDuration;
			wheelRotation = -targetRotation * (1 - (1 - progress) ** 10);
			if (wheelRotation <= -targetRotation + 0.001) {
				wheelRotation = -targetRotation;
				endSpin();
			}
		}

		const currentTickIndex = Math.floor(
			(wheelRotation - (Math.PI * 2) / wheelNumbers.length / 3 / 2) / ((Math.PI * 2) / wheelNumbers.length / 3),
		);
		if (currentTickIndex !== lastTickIndex) {
			SoundManager.playSound("tick");
			lastTickIndex = currentTickIndex;
		}

		canvasContext.save();
		canvasContext.translate(0, 2000);
		canvasContext.rotate(wheelRotation);
		canvasContext.translate(0, -2000);

		for (let i = 0; i < wheelNumbers.length * 3; i++) {
			const pocket = wheelNumbers[i % wheelNumbers.length]!;
			canvasContext.fillStyle = pocket.color;
			canvasContext.beginPath();
			canvasContext.arc(0, 0, 50, 0, Math.PI * 2, false);
			canvasContext.fill();
			canvasContext.fillStyle = "white";
			canvasContext.font = "bold 40px Roboto";
			const { width: textWidth } = canvasContext.measureText(String(pocket.number));
			canvasContext.fillText(String(pocket.number), -textWidth / 2, 15);
			canvasContext.translate(0, 2000);
			canvasContext.rotate((Math.PI * 2) / wheelNumbers.length / 3);
			canvasContext.translate(0, -2000);
		}

		canvasContext.restore();
		canvasContext.fillStyle = "white";
		canvasContext.fillRect(-1, -50, 2, 100);
	}
	const [, start] = createRaf(animateWheel);
	function keydown() {
		setIsShiftPressed(true);
	}
	function keyup() {
		setIsShiftPressed(false);
	}
	onMount(() => {
		canvasContext = canvas.getContext("2d")!;
		start();
		window.addEventListener("keydown", keydown);
		window.addEventListener("keyup", keyup);
	});

	onCleanup(() => {
		window.removeEventListener("keydown", keydown);
		window.removeEventListener("keyup", keyup);
	});

	return (
		<div class="center">
			<h1>Roulette</h1>
			<div class="coins">You have {Math.floor(user.tokens).toLocaleString()} chips.</div>

			<div>
				<button class="button" type="button" onclick={goToPreviousPage}>
					Back
				</button>
				<button class="button" type="button" onclick={tradeIn} classList={{ [styles.btnArrowHighlight]: user.tokens === 0 }}>
					Trade in Skins to Chips
				</button>
				<button class="button" type="button" onclick={openWithdrawPrompt}>
					Withdraw Chips to {getCurrency()}
				</button>
			</div>
			<canvas
				ref={(el) => {
					canvas = el;
				}}
				class={styles.wheel}
			/>
			<div class={styles.betInfo}>{betInfo()}</div>
			<div class={styles.betPicker}>
				<div
					class={`${styles.betPickerSlot} ${styles.betPickerSlot0}`}
					data-color="green"
					classList={{ [styles.betHighlighted]: selectedNumbers.has(0) }}
					data-tip="35:1"
					style={{ left: "0px", top: "0px", height: "120px" }}
					onclick={() => {
						selectedNumbers.clear();
						selectedNumbers.add(0);
					}}
				>
					0
				</div>
				{/* number bets */}
				<For each={Array.from({ length: 36 }, (_, i) => i + 1)}>
					{(num) => (
						<div
							class={`${styles.betPickerSlot}`}
							classList={{ [styles.betHighlighted]: selectedNumbers.has(num) }}
							data-tip="35:1"
							data-color={wheelNumbers.find((n) => n.number === num)?.color}
							style={{ left: `${40 + Math.floor((num - 1) / 3) * 40}px`, top: `${80 - ((num - 1) % 3) * 40}px` }}
							onclick={() => {
								selectedNumbers.clear();
								selectedNumbers.add(num);
							}}
						>
							{num}
						</div>
					)}
				</For>
				{/* dozen bets*/}
				<div
					class={`${styles.betPickerSlot} `}
					data-tip="2:1"
					style={{ left: "40px", top: "120px", height: "40px", width: "160px" }}
					onclick={() => {
						selectedNumbers.clear();
						[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					First 12th
				</div>
				<div
					class={styles.betPickerSlot}
					data-tip="2:1"
					style={{ left: "200px", top: "120px", height: "40px", width: "160px" }}
					onclick={() => {
						selectedNumbers.clear();
						[13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					Second 12th
				</div>
				<div
					class={styles.betPickerSlot}
					data-tip="2:1"
					style={{ left: "360px", top: "120px", height: "40px", width: "160px" }}
					onclick={() => {
						selectedNumbers.clear();
						[25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					Third 12th
				</div>
				{/* even odds */}
				<div
					class={styles.betPickerSlot}
					data-tip="1:1"
					style={{ left: "40px", top: "160px", height: "40px", width: "80px" }}
					onclick={() => {
						selectedNumbers.clear();
						[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					1-18
				</div>
				<div
					class={styles.betPickerSlot}
					data-tip="1:1"
					style={{ left: "120px", top: "160px", height: "40px", width: "80px" }}
					onclick={() => {
						selectedNumbers.clear();
						[2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					EVEN
				</div>
				<div
					class={`${styles.betPickerSlot} ${styles.rouletteBetPickerReds}`}
					data-tip="1:1"
					style={{ left: "200px", top: "160px", height: "40px", width: "80px" }}
					onclick={() => {
						selectedNumbers.clear();
						[1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					RED
				</div>
				<div
					class={`${styles.betPickerSlot} ${styles.betPickerBlacks}`}
					data-tip="1:1"
					style={{ left: "280px", top: "160px", height: "40px", width: "80px" }}
					onclick={() => {
						selectedNumbers.clear();
						[2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					BLACK
				</div>
				<div
					class={styles.betPickerSlot}
					data-tip="1:1"
					style={{ left: "360px", top: "160px", height: "40px", width: "80px" }}
					onclick={() => {
						selectedNumbers.clear();
						[1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31, 33, 35].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					ODD
				</div>
				<div
					class={styles.betPickerSlot}
					data-tip="1:1"
					style={{ left: "440px", top: "160px", height: "40px", width: "80px" }}
					onclick={() => {
						selectedNumbers.clear();
						[19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					19-36
				</div>
				{/* Column bets */}
				<div
					class={`${styles.betPickerSlot} ${styles.rouletteBetPickerVertical12}`}
					data-tip="2:1"
					style={{ left: "520px", top: "0px", height: "40px", width: "40px" }}
					onclick={() => {
						selectedNumbers.clear();
						[3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					2 to 1
				</div>
				<div
					class={`${styles.betPickerSlot} ${styles.rouletteBetPickerVertical12}`}
					data-tip="2:1"
					style={{ left: "520px", top: "40px", height: "40px", width: "40px" }}
					onclick={() => {
						selectedNumbers.clear();
						[2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					2 to 1
				</div>
				<div
					class={`${styles.betPickerSlot} ${styles.rouletteBetPickerVertical12}`}
					data-tip="2:1"
					style={{ left: "520px", top: "80px", height: "40px", width: "40px" }}
					onclick={() => {
						selectedNumbers.clear();
						[1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34].forEach((n) => {
							selectedNumbers.add(n);
						});
					}}
				>
					2 to 1
				</div>
				{/* Spin button */}
				<div
					class={`${styles.betPickerSlot} ${styles.rouletteBetPickerVertical12}`}
					classList={{ disabled: gameState() === "spinning" }}
					aria-disabled={gameState() === "spinning"}
					style={{ left: "40px", top: "220px", height: "40px", width: "480px" }}
					onclick={() => {
						startSpin();
					}}
				>
					{isShiftPressed() ? "Fast Spin" : "Spin"}
				</div>
			</div>
			<div class={styles.history}>
				<For each={rouletteHistory()}>
					{(entry) => (
						<div class={`${styles.rouletteHistoryItem} roulette-history-item-${entry.result}`} data-color={entry.color}>
							{entry.number}
						</div>
					)}
				</For>
			</div>

			<div class="roulette-bet-amount-changer">
				<button
					class="button"
					type="button"
					onclick={() => {
						setBetAmount2(Math.floor(betAmount() / 2));
					}}
				>
					{"1/2x <-"}
				</button>
				<input
					class="input"
					type="number"
					value={betAmount()}
					onblur={(e) => {
						setBetAmount2(e.target.valueAsNumber);
					}}
				/>
				<button
					class="button"
					type="button"
					onclick={() => {
						setBetAmount2(Math.max(betAmount() * 2, 10));
					}}
				>
					{"-> 2x"}
				</button>
			</div>
		</div>
	);
}

const wheelNumbers = [
	{ color: "green", number: 0 },
	{ color: "red", number: 32 },
	{ color: "black", number: 15 },
	{ color: "red", number: 19 },
	{ color: "black", number: 4 },
	{ color: "red", number: 21 },
	{ color: "black", number: 2 },
	{ color: "red", number: 25 },
	{ color: "black", number: 17 },
	{ color: "red", number: 34 },
	{ color: "black", number: 6 },
	{ color: "red", number: 27 },
	{ color: "black", number: 13 },
	{ color: "red", number: 36 },
	{ color: "black", number: 11 },
	{ color: "red", number: 30 },
	{ color: "black", number: 8 },
	{ color: "red", number: 23 },
	{ color: "black", number: 10 },
	{ color: "red", number: 5 },
	{ color: "black", number: 24 },
	{ color: "red", number: 16 },
	{ color: "black", number: 33 },
	{ color: "red", number: 1 },
	{ color: "black", number: 20 },
	{ color: "red", number: 14 },
	{ color: "black", number: 31 },
	{ color: "red", number: 9 },
	{ color: "black", number: 22 },
	{ color: "red", number: 18 },
	{ color: "black", number: 29 },
	{ color: "red", number: 7 },
	{ color: "black", number: 28 },
	{ color: "red", number: 12 },
	{ color: "black", number: 35 },
	{ color: "red", number: 3 },
	{ color: "black", number: 26 },
] as { color: "green" | "black" | "red"; number: number }[];

// ==================== GAME STATE ====================

createPage(() => <RoulettePage />, "roulette");
